import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CTASection from "../components/CTASection";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Clock, User, ArrowRight } from "lucide-react";
import { blogPosts } from "../data/blogPosts";

const Blog = () => {
  useEffect(() => {
    document.title = "Smog Check Blog | Hamilton's Smog — Marin County Tips & Guides";
  }, []);

  const [featured, ...rest] = blogPosts;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Header */}
        <section className="bg-slate-950 text-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4">
            <Badge className="bg-green-600 hover:bg-green-600 text-white border-0 mb-4">Hamilton's Smog Blog</Badge>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-tight max-w-4xl">
              Smog Check Guides & Tips for <span className="text-green-500">Marin County Drivers</span>
            </h1>
            <p className="text-slate-300 mt-4 text-lg max-w-2xl">
              Everything you need to know about STAR-certified smog inspections, passing the first time, classic cars, diesel testing, and local Marin County service.
            </p>
          </div>
        </section>

        {/* Featured */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <Link to={`/blog/${featured.slug}`} className="grid lg:grid-cols-2 gap-8 group">
              <div className="rounded-2xl overflow-hidden aspect-[16/10] bg-slate-100">
                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="flex flex-col justify-center">
                <div className="flex gap-2 flex-wrap mb-3">
                  {featured.tags.slice(0, 3).map((t) => (
                    <Badge key={t} variant="outline" className="border-green-200 text-green-700 bg-green-50">{t}</Badge>
                  ))}
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight group-hover:text-green-600 transition-colors">
                  {featured.title}
                </h2>
                <p className="text-slate-600 mt-4 text-lg">{featured.excerpt}</p>
                <div className="flex items-center gap-4 text-sm text-slate-500 mt-5">
                  <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {featured.author}</span>
                  <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {featured.readTime}</span>
                  <span>{featured.date}</span>
                </div>
                <div className="mt-6 inline-flex items-center gap-2 text-green-600 font-bold">
                  Read guide <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* List */}
        <section className="py-8 pb-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.slug} className="group">
                  <Card className="card-hover border-slate-200 overflow-hidden h-full flex flex-col">
                    <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <CardContent className="p-6 flex-1 flex flex-col">
                      <div className="flex gap-2 flex-wrap mb-2">
                        {post.tags.slice(0, 2).map((t) => (
                          <Badge key={t} variant="outline" className="border-slate-200 text-slate-600 bg-slate-50 text-xs">{t}</Badge>
                        ))}
                      </div>
                      <h3 className="font-black text-slate-900 text-lg group-hover:text-green-600 transition-colors leading-snug">{post.title}</h3>
                      <p className="text-slate-600 text-sm mt-2 line-clamp-3 flex-1">{post.excerpt}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-4">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
                        <span>{post.date}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
